/* global process */

import { createServerFn } from '@tanstack/react-start';

const PEOPLEFORCE_API_BASE_URL = 'https://app.peopleforce.io/api/careers/v1';
const PEOPLEFORCE_LIST_LIMIT = '1000';
const PEOPLEFORCE_DEFAULT_LOCALE = 'en';
const PEOPLEFORCE_LOCALE_QUERY_PARAM = 'locale';
const PEOPLEFORCE_LOCALE_QUERY_PARAM_ALIASES = [
  PEOPLEFORCE_LOCALE_QUERY_PARAM,
  'localization',
] as const;
const DEFAULT_PEOPLEFORCE_FETCH_TIMEOUT_MS = 2500;
const PEOPLEFORCE_FETCH_TIMEOUT_ENV = 'PEOPLEFORCE_FETCH_TIMEOUT_MS';
const PEOPLEFORCE_LOG_PREFIX = '[peopleforce]';

const serializeError = (error: unknown) => {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
    };
  }

  return {
    errorName: typeof error,
    errorMessage: String(error),
  };
};

const logPeopleForceWarning = (
  message: string,
  details?: Record<string, unknown>
) => {
  console.warn(`${PEOPLEFORCE_LOG_PREFIX} ${message}`, details ?? {});
};

const logPeopleForceError = (
  message: string,
  details?: Record<string, unknown>
) => {
  console.error(`${PEOPLEFORCE_LOG_PREFIX} ${message}`, details ?? {});
};

type JsonRecord = Record<string, unknown>;

export type PeopleForceVacancyStatus =
  | 'ready'
  | 'empty'
  | 'error'
  | 'unconfigured';

export interface PeopleForceVacancy {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  department: string | null;
  employmentType: string | null;
  applyUrl: string | null;
}

export interface PeopleForceVacanciesPayload {
  status: PeopleForceVacancyStatus;
  vacancies: PeopleForceVacancy[];
}

export interface PeopleForceVacanciesCountPayload {
  status: PeopleForceVacancyStatus;
  count: number | null;
}

interface VacancyLookups {
  employmentTypes: Record<string, string>;
  locations: Record<string, string>;
  departments: Record<string, string>;
}

const EMPTY_VACANCY_LOOKUPS: VacancyLookups = {
  employmentTypes: {},
  locations: {},
  departments: {},
};

const isRecord = (value: unknown): value is JsonRecord => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const toText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return null;
};

const hasPeopleForceLocaleParam = (url: URL) =>
  PEOPLEFORCE_LOCALE_QUERY_PARAM_ALIASES.some((param) =>
    url.searchParams.has(param)
  );

const ensurePeopleForceDefaultLocale = (url: URL) => {
  if (!hasPeopleForceLocaleParam(url)) {
    url.searchParams.set(
      PEOPLEFORCE_LOCALE_QUERY_PARAM,
      PEOPLEFORCE_DEFAULT_LOCALE
    );
  }
};

const withPeopleForceDefaultLocale = (value: string | null) => {
  if (!value) {
    return null;
  }

  try {
    const url = new URL(value);
    ensurePeopleForceDefaultLocale(url);
    return url.toString();
  } catch {
    return null;
  }
};

const decodeHtmlEntities = (value: string) => {
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');
};

const stripHtml = (value: string) => {
  return decodeHtmlEntities(
    value
      .replace(/<\s*(strong|b)[^>]*>([\s\S]*?)<\s*\/\s*\1\s*>/gi, '**$2**')
      .replace(/<\s*h[1-6][^>]*>([\s\S]*?)<\s*\/\s*h[1-6]\s*>/gi, '\n## $1\n')
      .replace(/<(br|hr)\s*\/?>/gi, '\n')
      .replace(/<li[^>]*>/gi, '\n- ')
      .replace(/<\/(p|div|section|article|li|ul|ol)>/gi, '\n')
      .replace(/<[^>]+>/g, ' ')
  )
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n[-*•]\s*\n(?=\S)/g, '\n- ')
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

const getCollection = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!isRecord(payload)) {
    return [];
  }

  const collectionKeys = ['data', 'items', 'results', 'vacancies', 'records'];

  for (const key of collectionKeys) {
    const candidate = payload[key];

    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (isRecord(candidate)) {
      const nested = getCollection(candidate);

      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
};

const findFirstText = (item: JsonRecord, keys: string[]) => {
  for (const key of keys) {
    const value = toText(item[key]);

    if (value) {
      return value;
    }
  }

  return null;
};

const findNestedLabel = (value: unknown) => {
  if (!isRecord(value)) {
    return null;
  }

  return findFirstText(value, ['name', 'title', 'label', 'value']);
};

const findLookupLabel = (
  item: JsonRecord,
  nestedKeys: string[],
  idKeys: string[],
  lookup: Record<string, string>
) => {
  for (const nestedKey of nestedKeys) {
    const nestedValue = findNestedLabel(item[nestedKey]);

    if (nestedValue) {
      return nestedValue;
    }
  }

  for (const idKey of idKeys) {
    const lookupId = toText(item[idKey]);

    if (lookupId && lookup[lookupId]) {
      return lookup[lookupId];
    }
  }

  return null;
};

const shouldSkipVacancy = (item: JsonRecord) => {
  const rawState = findFirstText(item, [
    'status',
    'state',
    'vacancy_status',
    'vacancyStatus',
  ]);

  if (!rawState) {
    return false;
  }

  return ['archived', 'closed', 'draft', 'filled', 'inactive'].includes(
    rawState.toLowerCase()
  );
};

export const normalizePeopleForceLookup = (payload: unknown) => {
  return getCollection(payload).reduce<Record<string, string>>((acc, entry) => {
    if (!isRecord(entry)) {
      return acc;
    }

    const id = findFirstText(entry, ['id', 'uuid', 'value']);
    const label = findFirstText(entry, ['name', 'title', 'label', 'value']);

    if (id && label) {
      acc[id] = label;
    }

    return acc;
  }, {});
};

export const normalizePeopleForceVacancies = (
  payload: unknown,
  { locations, departments, employmentTypes }: VacancyLookups
) => {
  return getCollection(payload).reduce<PeopleForceVacancy[]>((acc, entry) => {
    if (!isRecord(entry) || shouldSkipVacancy(entry)) {
      return acc;
    }

    const id =
      findFirstText(entry, ['id', 'uuid', 'external_id', 'externalId']) ??
      crypto.randomUUID();
    const title = findFirstText(entry, [
      'title',
      'name',
      'position',
      'job_title',
      'jobTitle',
    ]);

    if (!title) {
      return acc;
    }
    const description = findFirstText(entry, [
      'summary',
      'short_description',
      'shortDescription',
      'description',
      'content',
      'about_role',
      'aboutRole',
    ]);
    const location =
      findFirstText(entry, ['location', 'location_name', 'locationName']) ??
      findLookupLabel(
        entry,
        ['location'],
        ['location_id', 'locationId'],
        locations
      );
    const department =
      findFirstText(entry, [
        'department',
        'department_name',
        'departmentName',
        'team',
        'team_name',
        'teamName',
        'subdivision',
        'subdivision_name',
        'subdivisionName',
      ]) ??
      findLookupLabel(
        entry,
        ['department', 'team', 'subdivision'],
        [
          'department_id',
          'departmentId',
          'team_id',
          'teamId',
          'subdivision_id',
          'subdivisionId',
        ],
        departments
      );
    const employmentType =
      findFirstText(entry, [
        'employment_type',
        'employmentType',
        'employment_type_name',
        'employmentTypeName',
      ]) ??
      findLookupLabel(
        entry,
        ['employment_type', 'employmentType'],
        ['employment_type_id', 'employmentTypeId'],
        employmentTypes
      );
    const applyUrlCandidate = findFirstText(entry, [
      'apply_url',
      'applyUrl',
      'public_url',
      'publicUrl',
      'career_page_url',
      'careerPageUrl',
      'url',
    ]);

    acc.push({
      id,
      title,
      description: description ? stripHtml(description) : null,
      location,
      department,
      employmentType,
      applyUrl: withPeopleForceDefaultLocale(applyUrlCandidate),
    });

    return acc;
  }, []);
};

export const buildPeopleForceUrl = (resource: string) => {
  const url = new URL(`${PEOPLEFORCE_API_BASE_URL}/${resource}`);
  url.searchParams.set('per_page', PEOPLEFORCE_LIST_LIMIT);
  ensurePeopleForceDefaultLocale(url);
  return url.toString();
};

const getPeopleForceFetchTimeoutMs = () => {
  const rawTimeout = process.env[PEOPLEFORCE_FETCH_TIMEOUT_ENV]?.trim();

  if (!rawTimeout) {
    return DEFAULT_PEOPLEFORCE_FETCH_TIMEOUT_MS;
  }

  const timeoutMs = Number(rawTimeout);

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return DEFAULT_PEOPLEFORCE_FETCH_TIMEOUT_MS;
  }

  return timeoutMs;
};

const logPeopleForceLookupFailure = (
  resource: string,
  result: PromiseSettledResult<unknown>
) => {
  if (result.status !== 'rejected') {
    return;
  }

  logPeopleForceWarning('PeopleForce lookup request failed', {
    resource,
    timeoutMs: getPeopleForceFetchTimeoutMs(),
    ...serializeError(result.reason),
  });
};

const fetchPeopleForce = async (resource: string, apiKey: string) => {
  const timeoutMs = getPeopleForceFetchTimeoutMs();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(buildPeopleForceUrl(resource), {
      headers: {
        Accept: 'application/json',
        'X-API-KEY': apiKey,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `PeopleForce request failed for ${resource}: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(
        `PeopleForce request timed out for ${resource} after ${timeoutMs}ms`
      );
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
};

const getPeopleForceApiKey = () => process.env.PEOPLEFORCE_API_KEY?.trim();

const getVacancyStatus = (count: number): PeopleForceVacancyStatus =>
  count > 0 ? 'ready' : 'empty';

const fetchOpenVacanciesPayload =
  async (): Promise<PeopleForceVacanciesPayload> => {
    const apiKey = getPeopleForceApiKey();

    if (!apiKey) {
      logPeopleForceWarning('PEOPLEFORCE_API_KEY is not configured', {
        context: 'open_vacancies',
      });

      return {
        status: 'unconfigured',
        vacancies: [],
      };
    }

    const [vacanciesResult, locationsResult, employmentTypesResult] =
      await Promise.allSettled([
        fetchPeopleForce('vacancies', apiKey),
        fetchPeopleForce('locations', apiKey),
        fetchPeopleForce('employment_types', apiKey),
      ]);

    if (vacanciesResult.status === 'rejected') {
      logPeopleForceError('PeopleForce vacancies request failed', {
        resource: 'vacancies',
        timeoutMs: getPeopleForceFetchTimeoutMs(),
        ...serializeError(vacanciesResult.reason),
      });

      return {
        status: 'error',
        vacancies: [],
      };
    }

    logPeopleForceLookupFailure('locations', locationsResult);
    logPeopleForceLookupFailure('employment_types', employmentTypesResult);

    const locations =
      locationsResult.status === 'fulfilled'
        ? normalizePeopleForceLookup(locationsResult.value)
        : {};
    const employmentTypes =
      employmentTypesResult.status === 'fulfilled'
        ? normalizePeopleForceLookup(employmentTypesResult.value)
        : {};
    const vacancies = normalizePeopleForceVacancies(vacanciesResult.value, {
      locations,
      departments: EMPTY_VACANCY_LOOKUPS.departments,
      employmentTypes,
    });

    return {
      status: getVacancyStatus(vacancies.length),
      vacancies,
    };
  };

export const getOpenVacancies = createServerFn({ method: 'GET' }).handler(
  fetchOpenVacanciesPayload
);

export const getOpenVacanciesCount = createServerFn({ method: 'GET' }).handler(
  async (): Promise<PeopleForceVacanciesCountPayload> => {
    const apiKey = getPeopleForceApiKey();

    if (!apiKey) {
      logPeopleForceWarning('PEOPLEFORCE_API_KEY is not configured', {
        context: 'open_vacancies_count',
      });

      return {
        status: 'unconfigured',
        count: null,
      };
    }

    try {
      const payload = await fetchPeopleForce('vacancies', apiKey);
      const vacancies = normalizePeopleForceVacancies(
        payload,
        EMPTY_VACANCY_LOOKUPS
      );

      return {
        status: getVacancyStatus(vacancies.length),
        count: vacancies.length,
      };
    } catch (error) {
      logPeopleForceError('PeopleForce vacancy count request failed', {
        resource: 'vacancies',
        timeoutMs: getPeopleForceFetchTimeoutMs(),
        ...serializeError(error),
      });

      return {
        status: 'error',
        count: null,
      };
    }
  }
);
